import type { AuthProviderSummary } from '@ralph/contracts'
import { useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import { Lock } from 'lucide-react'
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ApiRequestError, apiGet } from '@/api/client.ts'
import { brand } from '@/brand/active.ts'
import { AssistantMark } from '@/brand/BrandMarks.tsx'
import { Desk } from '@/components/shell/Desk.tsx'
import { Button, Input, Panel, Spinner } from '@/components/ui/primitives.tsx'
import { useLogin } from '@/hooks/use-api.ts'
import { describeIssue, LanguageSwitcher, useT } from '@/i18n/index.ts'
import type { Messages } from '@/i18n/messages/index.ts'

/**
 * Sign-in.
 *
 * Federated providers are the real path: the button navigates to the API, which begins the
 * authorization-code + PKCE flow and pins the chosen issuer server-side. The password form
 * only appears when the deployment enables it, which the environment schema refuses in
 * production — a shared password is not an identity.
 *
 * This is the one route `AppShell` does not wrap, so the page paints its own desk and floats a
 * single panel on it — the same room, before there is anything to put in it. It refuses to
 * grow the things a sign-in screen accretes: no "forgot password" link with nothing behind it,
 * no provider logos guessed from `iconHint`, and no second iridescent control. Exactly one
 * thing on the screen wears the gradient, and it is whichever mechanism this deployment
 * actually intends people to use.
 */

/**
 * A federated provider is a real navigation to the API, so it stays an anchor and wears the
 * button shape by hand rather than through the `Button` primitive.
 *
 * Only the first one is the hero. A column of identical gradient buttons would say every
 * issuer is the one to pick, which is the opposite of what a deployment listing a second one
 * means by listing it second.
 */
const providerLinkClass = (index: number) =>
  clsx(
    'flex h-[38px] w-full select-none items-center justify-center gap-2 rounded-full px-5',
    'text-[14px] font-semibold tracking-[-0.014em]',
    'transition-[transform,box-shadow,border-color] duration-200 ease-out hover:-translate-y-px',
    index === 0
      ? 'bg-[image:var(--irid)] text-white shadow-[var(--irid-glow)] hover:shadow-[var(--irid-glow-strong)]'
      : 'border border-rule-strong bg-card-solid text-fg hover:shadow-glass'
  )

/**
 * Why the sign-in was refused, in the reader's language.
 *
 * `describeApiError` is deliberately NOT used here, and this is the one screen in the product
 * where that is true. It answers a 401 with "your session has ended — sign in again", which is
 * the right sentence everywhere except on the page that IS signing in: here a 401 is the
 * refusal itself (`incorrect password`, a deactivated account), so the issue is rendered
 * directly and this page's own sentence is the fallback.
 *
 * Everything else is the ordinary seam — a `messageKey` renders from the catalogue, a refusal
 * this build has no key for falls back to the server's English, which distinguishes a wrong
 * password from a closed account and is better than a generic line (PRD-27 D-4, LOC-033).
 */
const refusalSentence = (messages: Messages, failure: unknown): string => {
  if (!(failure instanceof ApiRequestError)) return messages.login.signInFailed
  const issue = failure.issues[0]
  if (!issue) return messages.login.signInFailed
  return describeIssue(messages, issue) ?? (issue.message || messages.login.signInFailed)
}

export const LoginPage = () => {
  // In the body, never at module scope. This screen is rendered before there is an account
  // to read a preference from, and the switcher in its footer has to change the words on it
  // immediately — a capture taken at import would leave every one of them frozen (PRD-27
  // F-3, F-23).
  const messages = useT()
  const t = messages.login
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const login = useLogin()
  const [email, setEmail] = useState('ada@ralph.local')
  const [password, setPassword] = useState('')

  const providers = useQuery({
    queryKey: ['auth', 'providers'],
    queryFn: () => apiGet<{ providers: AuthProviderSummary[] }>('/api/auth/providers')
  })

  const federated = providers.data?.providers.filter((p) => p.kind !== 'local') ?? []
  const localEnabled = providers.data?.providers.some((p) => p.kind === 'local') ?? false
  const error = params.get('error')
  const redirectTo = params.get('redirectTo') ?? '/'

  const signInLocally = async () => {
    await login.mutateAsync({ email, password })
    navigate(redirectTo)
  }

  return (
    <div className="relative flex min-h-dvh items-center justify-center bg-desk px-4 py-10 text-fg sm:px-6">
      <Desk />

      {/* `relative` is load-bearing: the aurora is absolutely positioned, so anything left in
          the normal flow paints underneath it rather than on the glass. */}
      <div className="glass rise relative w-full max-w-[412px] rounded-panel p-6 shadow-frame sm:p-8">
        <div className="flex flex-col items-center text-center">
          {/* The bob sits on a wrapper because the shared Orb takes a size and a class, not a
              style. The reduced-motion block in surfaces.css still stills it — that override
              is `!important` precisely so an inline animation cannot outrank the preference. */}
          <span className="inline-block" style={{ animation: 'bob 7s ease-in-out infinite' }}>
            <AssistantMark hero size={72} />
          </span>

          <h1 className="mt-6 text-[32px] sm:text-[38px]">{t.title}</h1>
          {/* The standfirst is this deployment's own sentence, and it is prose rather than a
              name — so it comes from the `brand` namespace in the language on screen, not
              from the profile (PRD-27 D-12). It flips with the switcher in the footer. */}
          <p className="mt-2.5 text-pretty text-[14px] leading-[1.55] text-fg-muted">
            {messages.brand.profiles[brand.id].tagline}
          </p>
        </div>

        {/* The federated flow refuses by REDIRECTING here with its sentence in the query
            string, so this is the server's English and there is nothing in the URL to look a
            translation up by. It stays prose until the callback carries the `messageKey`
            beside it — the one refusal on this screen the client cannot fix alone. */}
        {error && (
          <div className="mt-6 rounded-card border border-alert/40 bg-alert-soft px-3.5 py-3 shadow-glass">
            <p className="text-[13px] leading-relaxed">{decodeURIComponent(error)}</p>
          </div>
        )}

        {providers.isLoading ? (
          <div className="mt-8 flex items-center justify-center gap-2 text-[13px] text-fg-muted">
            <Spinner size={13} />
            {t.checkingMethods}
          </div>
        ) : (
          <div className="fade-in mt-7 space-y-2.5">
            {federated.map((provider, index) => (
              <a
                key={provider.slug}
                href={`/api/auth/login?provider=${provider.slug}&redirectTo=${encodeURIComponent(redirectTo)}`}
                className={providerLinkClass(index)}
              >
                {t.continueWith(provider.label)}
              </a>
            ))}

            {federated.length > 0 && localEnabled && (
              <div className="flex items-center gap-3 py-1">
                <span className="h-px flex-1 bg-rule" />
                <span className="eyebrow">{t.or}</span>
                <span className="h-px flex-1 bg-rule" />
              </div>
            )}

            {localEnabled && (
              <form
                className="space-y-3"
                onSubmit={(e) => {
                  e.preventDefault()
                  void signInLocally()
                }}
              >
                {/* The label/`htmlFor` pair is the contract both browser suites authenticate
                    through (`getByLabel('Email')`). A placeholder floating inside the field
                    would fail every test in the repo at its first step. */}
                <div>
                  <label htmlFor="login-email" className="eyebrow mb-1.5 block text-fg-muted">
                    {t.emailLabel}
                  </label>
                  <Input
                    id="login-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="username"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="login-password" className="eyebrow mb-1.5 block text-fg-muted">
                    {t.passwordLabel}
                  </label>
                  <Input
                    id="login-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                  />
                </div>

                {/* The refusal, and `role="alert"` because one that only appears visually is
                    a dead end for anyone not looking at that spot. */}
                {login.isError && (
                  <p role="alert" className="text-[13px] leading-relaxed text-alert">
                    {refusalSentence(messages, login.error)}
                  </p>
                )}

                <Button
                  type="submit"
                  variant={federated.length > 0 ? 'secondary' : 'primary'}
                  size="lg"
                  className="mt-1 w-full"
                  disabled={login.isPending}
                  icon={login.isPending ? <Spinner size={13} /> : undefined}
                >
                  {login.isPending ? t.signingIn : t.submit}
                </Button>

                <p className="text-center text-[12.5px] leading-relaxed text-fg-subtle">{t.devNotice}</p>
              </form>
            )}

            {/*
              An unreachable API and a deployment with nothing configured both leave these
              lists empty, and they are NOT the same dead end: one is temporary and one needs
              an administrator to edit configuration. Collapsing them tells whoever is looking
              at a service outage to go and set an environment variable that is already set —
              which is a wasted afternoon, and it is what this screen used to do.
            */}
            {providers.isError ? (
              <Panel solid className="px-4 py-3.5">
                <p role="alert" className="text-[13px] leading-relaxed text-fg-muted">
                  {t.unreachable}
                </p>
              </Panel>
            ) : (
              federated.length === 0 &&
              !localEnabled && (
                <Panel solid className="px-4 py-3.5">
                  {/* Two entries around one `<code>`: the catalogue is data and never
                      markup, so the view composes the halves rather than the string
                      carrying a tag (PRD-27, "catalogues are data, not markup"). */}
                  <p className="text-[13px] leading-relaxed text-fg-muted">
                    {t.noMethod.before}{' '}
                    <code className="readout rounded-[6px] border border-rule bg-desk px-1.5 py-0.5">
                      AUTH_PROVIDERS_JSON
                    </code>{' '}
                    {t.noMethod.after}
                  </p>
                </Panel>
              )
            )}
          </div>
        )}

        {/*
          The switcher's second home, and the reason it is a requirement rather than a
          convenience: `/login` sits outside `AppShell` (PRD-27 F-23), so the copy in the
          workspace menu is unreachable from here — and a person on a Gi deployment must be
          able to change the language of this screen before they have an account that could
          have remembered the choice for them (LOC-065).

          The two words in it are labelled in their own languages, so it stays legible to
          somebody who cannot read the one currently on screen.

          The on-prem badge is `shell`'s: it is the same chrome saying the same thing about
          the same deployment, and two entries would be two translations of one claim.
        */}
        <div className="mt-7 flex flex-col items-center gap-3 border-t border-rule pt-5">
          <LanguageSwitcher />
          <span className="flex items-center gap-2">
            <Lock size={11} strokeWidth={2.4} className="text-grounded" aria-hidden />
            <span className="eyebrow text-fg-muted" title={messages.shell.onPremTitle}>
              {brand.productName} · {messages.shell.onPrem}
            </span>
          </span>
        </div>
      </div>
    </div>
  )
}
